/**
 * Service detail screen (DNKADM-0055 U2 + U3; cards DNKADM-0493) — one `v2_service`, routed
 * at `/services/:serviceId` under `RequireAdmin`.
 *
 * **Read-only cards, one Edit each (DNKADM-0493).** The page is five summary cards, top to
 * bottom: Basics; Where and who; Booking rules; Lengths and prices; Special prices. Each card
 * shows its values as text and has ONE Edit that opens a right-side {@link EditDrawer} with
 * one Save and one Cancel; there is no Save anywhere else on the page. The drawers reuse the
 * existing editors: {@link ServiceFields} (the {@link ServiceDialog} form, split by card), the
 * {@link AssignmentEditor} in its embedded mode, the length drawer
 * ({@link VariationsPanel} → `VariationDialog`) and the three price editors
 * ({@link SpecialPricesCard}). Same calls, same bodies, same validation as the controls they
 * replace: a drawer's Save runs its parts in order (the service PUT, then each changed
 * assignment's whole-set PUT) and stops at the first that fails, with the drawer left open on
 * that part's plain error. Success closes it and the page re-reads.
 *
 * **Header.** Name, the status chip (Draft / Turned off / Active, its meaning on hover or
 * tap), and a More menu: Duplicate (the copy starts as a Draft), Turn on (publish a Draft,
 * reactivate a turned-off service) / Turn off (deactivate), and Remove (asks first).
 *
 * **Can't be booked (DNKADM-0498).** When the server calls a live service not bookable
 * (`setupHealth`, DNKAPI-0715), the "Where and who" card opens with the same line the
 * Services list shows ("Can’t be booked yet:" and the plain reasons, {@link notBookableLine}).
 * The card then never collapses to its one-line empty state, so the line stays on screen.
 *
 * **Empty-pool warning (§4.7 / §5.5).** When a required dimension (`MEMBER_SELECTS`/
 * `ROUND_ROBIN`) has no eligible entity covering an offered location, the page says so under
 * the header — the booking-time mirror of `SERVICE_HAS_NO_ASSIGNABLE_STAFF/RESOURCE`.
 *
 * **Assignment wipe guards (DNKADM-0395/0397)** live in {@link AssignmentEditor} and hold in
 * the drawers unchanged: an unloaded selection holds the drawer's Save off, and clearing a
 * coach or bay pool the service uses asks first.
 */
import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { IconAction } from '@/components/IconAction';
import { useResource, type Resource } from '@/hooks/useResource';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { useToast } from '@/components/useToast';
import {
  copyService,
  deactivateService,
  getService,
  getServiceCategories,
  getServiceLocations,
  getServiceResources,
  getServiceStaff,
  publishService,
  reactivateService,
  removeService,
  setServiceCategories,
  setServiceLocations,
  setServiceResources,
  setServiceStaff,
  type V2Service,
} from '@/api/services';
import { serverMessage } from '@/api/serverMessage';
import { getServiceCategoryOptions } from '@/api/serviceCategories';
import { getStaffOptions, getStaffLocations } from '@/api/staff';
import { getResourceOptions, getResourceLocations } from '@/api/resources';
import { listLocations } from '@/api/locations';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { PickListError } from '@/api/picklists';
import { LEAD_TIME_BEHAVIOUR, leadTimeMinutesLabel } from '@/domain/leadTime';
import { AssignmentEditor, type AssignmentEditorHandle, type IdNameOption } from './AssignmentEditor';
import { VariationsPanel } from './VariationsPanel';
import { SpecialPricesCard } from './SpecialPricesCard';
import { ServiceFields, type ServiceFormHandle } from './ServiceDialog';
import { ServiceStatusChip } from './ServiceStatusChip';
import { EditDrawer } from './EditDrawer';
import { SummaryCard, SummaryRow } from './SummaryCard';
import { SERVICE_STATUS_WORDS, notBookableLine, paymentModeSummary, pickModeLabel } from './serviceLabels';
import { ScreenError } from '@/components/ScreenError';

/** Display name for a service (its name, falling back to the id). */
function serviceName(service: V2Service): string {
  return service.name.length > 0 ? service.name : service.id;
}

/**
 * Load ALL owned locations (every status) as fail-closed `{ id, name, isActive }`
 * options (no shared location pick-list exists, so this local wrapper mirrors the
 * others' fail-closed contract — a non-array success or a thrown error becomes a
 * `PickListError`). Loading every status (DNKADM-0134) keeps an already-assigned but
 * since-deactivated location visible in the editor; `AssignmentEditor` tags it inline.
 */
async function getLocationOptions(): Promise<IdNameOption[]> {
  try {
    const locs = await listLocations({ status: 'all' });
    return locs.map((l) => ({
      id: l.id,
      name: l.name.length > 0 ? l.name : l.id,
      isActive: l.isActive,
    }));
  } catch (cause) {
    throw new PickListError('locations', cause);
  }
}

/**
 * Compute the empty-pool warnings for the required dimensions. For each required
 * dimension (staff/resource ≠ NONE) and each offered location, at least one assigned
 * entity must also be assigned to that location; otherwise the location is uncovered.
 * Returns a per-dimension message when there is no assigned entity at all or an
 * offered location is uncovered.
 */
async function computeEmptyPoolWarnings(
  service: V2Service,
  locationNameById: Map<string, string>,
): Promise<string[]> {
  const warnings: string[] = [];
  const offeredLocationIds = await getServiceLocations(service.id);

  async function checkDimension(
    noun: { one: string; many: string },
    assignedIds: string[],
    getEntityLocations: (id: string) => Promise<string[]>,
  ): Promise<void> {
    if (assignedIds.length === 0) {
      warnings.push(`No ${noun.many} are assigned, but this service needs a ${noun.one}.`);
      return;
    }
    if (offeredLocationIds.length === 0) return; // location warning handled elsewhere
    // Map each offered location to whether any assigned entity covers it.
    const entityLocations = await Promise.all(
      assignedIds.map((id) => getEntityLocations(id).catch(() => [] as string[])),
    );
    const covered = new Set<string>();
    for (const locs of entityLocations) for (const l of locs) covered.add(l);
    const uncovered = offeredLocationIds.filter((l) => !covered.has(l));
    if (uncovered.length > 0) {
      const names = uncovered.map((l) => locationNameById.get(l) ?? l).join(', ');
      warnings.push(`No ${noun.one} at: ${names}. Bookings there will find no availability.`);
    }
  }

  if (service.staffAssignment !== 'NONE') {
    const staffIds = await getServiceStaff(service.id);
    await checkDimension({ one: 'coach', many: 'coaches' }, staffIds, getStaffLocations);
  }
  if (service.resourceAssignment !== 'NONE') {
    const resourceIds = await getServiceResources(service.id);
    await checkDimension({ one: 'bay', many: 'bays' }, resourceIds, getResourceLocations);
  }
  return warnings;
}

/** The drawer each card opens (the Lengths and Special prices cards own their own). */
type CardDrawer = 'basics' | 'who' | 'rules';

const DRAWER_TITLES: Record<CardDrawer, string> = {
  basics: 'Edit basics',
  who: 'Edit where and who',
  rules: 'Edit booking rules',
};

/** Anything a drawer saves through: the service form or an embedded assignment editor. */
type SavePart = RefObject<ServiceFormHandle | AssignmentEditorHandle | null>;

/**
 * What a card can say about an assigned set. `ready` only when BOTH the assigned ids and the
 * pick-list that names them have loaded — a failed or pending read is never shown as an empty
 * set ("No coach can take this" on a service that has coaches would be false).
 */
type AssignedView = { state: 'loading' } | { state: 'error' } | { state: 'ready'; names: string[] };

/** Names for assigned ids, resolved against a loaded pick-list ("Inactive" tagged). */
function assignedView(ids: Resource<string[]>, list: PickList<IdNameOption>): AssignedView {
  if (ids.status === 'error' || list.status === 'error') return { state: 'error' };
  // A re-read keeps the last loaded ids on screen (`data` survives a refetch).
  if (ids.data === null || list.items === null) return { state: 'loading' };
  const byId = new Map(list.items.map((o) => [o.id, o]));
  return {
    state: 'ready',
    names: ids.data.map((id) => {
      const opt = byId.get(id);
      if (!opt) return 'Unknown';
      const name = opt.name || opt.id;
      return opt.isActive ? name : `${name} (Inactive)`;
    }),
  };
}

/** The view as text: "Couldn't load", "—" while loading, the names, or the empty sentence. */
function listOr(view: AssignedView, none: string): string {
  if (view.state === 'error') return 'Couldn’t load';
  if (view.state === 'loading') return '—';
  return view.names.length > 0 ? view.names.join(', ') : none;
}

const isEmptySet = (view: AssignedView) => view.state === 'ready' && view.names.length === 0;

function guestRules(service: V2Service): string {
  if (!service.allowGuests) return 'Not allowed';
  return service.maxGuests > 0 ? `Allowed (up to ${service.maxGuests})` : 'Allowed (no limit)';
}

export function ServiceDetail() {
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const showToast = useToast();
  const navigate = useNavigate();

  const service = useResource(
    useCallback(() => getService(serviceId), [serviceId]),
    [serviceId],
  );

  // The active locations (for the empty-pool warning's location-name resolution).
  const locations = useResource(
    useCallback(() => listLocations({ status: 'active' }), []),
    [],
  );
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations.data ?? []) map.set(l.id, l.name.length > 0 ? l.name : l.id);
    return map;
  }, [locations.data]);

  // Credit-bucket id → name for the Booking rules card (DNKADM-0295). A load failure
  // degrades to an empty map (never blocks the page); an unresolvable id renders `—`.
  const buckets = useResource(
    useCallback(() => listCreditBuckets({ status: 'all' }), []),
    [],
  );
  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of (buckets.data ?? []) as CreditBucket[]) {
      map.set(b.id, b.name.length > 0 ? b.name : b.id);
    }
    return map;
  }, [buckets.data]);

  const serviceData = service.data;
  // Empty-pool warnings recompute when the service or location names change.
  const warnings = useResource(
    useCallback(
      () =>
        serviceData
          ? computeEmptyPoolWarnings(serviceData, locationNameById)
          : Promise.resolve<string[]>([]),
      [serviceData, locationNameById],
    ),
    [serviceData, locationNameById],
  );

  // The assigned sets the cards show (the same GETs the editors make).
  const assignedCategories = useResource(
    useCallback(() => getServiceCategories(serviceId), [serviceId]),
    [serviceId],
  );
  const assignedLocations = useResource(
    useCallback(() => getServiceLocations(serviceId), [serviceId]),
    [serviceId],
  );
  // The service's ASSIGNED (eligible) staff ids — shown on the card and the candidate set
  // for coach prices (only an assigned coach may carry one, §5.5).
  const assignedStaff = useResource(
    useCallback(() => getServiceStaff(serviceId), [serviceId]),
    [serviceId],
  );
  const assignedResources = useResource(
    useCallback(() => getServiceResources(serviceId), [serviceId]),
    [serviceId],
  );

  // Fail-closed pick-lists for the four assignment editors (and the cards' names).
  const categoryList = usePickList(useCallback(() => getServiceCategoryOptions(), []), []);
  const locationList = usePickList(useCallback(() => getLocationOptions(), []), []);
  const staffList = usePickList(
    useCallback(() => getStaffOptions({ includeInactive: true }), []),
    [],
  );
  const resourceList = usePickList(
    useCallback(() => getResourceOptions({ includeInactive: true }), []),
    [],
  );

  // Resolve the assigned staff ids to `{ id, name }` against the roster — the coach price
  // editor offers ONLY the service's coaches, never the whole roster.
  const staffOptions = useMemo<IdNameOption[]>(() => {
    const roster = staffList.items ?? [];
    const assignedIds = new Set(assignedStaff.data ?? []);
    return roster.filter((s) => assignedIds.has(s.id));
  }, [staffList.items, assignedStaff.data]);

  // Bumped by the Lengths card on every change so the Special prices card re-reads.
  const [lengthsVersion, setLengthsVersion] = useState(0);
  const onLengthsChanged = useCallback(() => setLengthsVersion((v) => v + 1), []);

  // ---- Card drawers ----
  const [drawer, setDrawer] = useState<CardDrawer | null>(null);
  const formRef = useRef<ServiceFormHandle>(null);
  const categoriesRef = useRef<AssignmentEditorHandle>(null);
  const locationsRef = useRef<AssignmentEditorHandle>(null);
  const staffRef = useRef<AssignmentEditorHandle>(null);
  const resourcesRef = useRef<AssignmentEditorHandle>(null);
  // Which embedded editors have NOT loaded their selection — the drawer's Save stays off
  // while any has not (DNKADM-0395 U1: an unloaded set must never be saved over the real one).
  const [unloaded, setUnloaded] = useState<Record<string, boolean>>({});
  const statusFor = useMemo(() => {
    const make = (key: string) => (s: { loaded: boolean }) =>
      setUnloaded((prev) => (prev[key] === !s.loaded ? prev : { ...prev, [key]: !s.loaded }));
    return {
      categories: make('categories'),
      locations: make('locations'),
      staff: make('staff'),
      resources: make('resources'),
    };
  }, []);
  const anyUnloaded = Object.values(unloaded).some(Boolean);

  const refetchService = service.refetch;
  const refetchCategories = assignedCategories.refetch;
  const refetchLocations = assignedLocations.refetch;
  const refetchStaff = assignedStaff.refetch;
  const refetchResources = assignedResources.refetch;
  const refreshPage = useCallback(() => {
    refetchService();
    refetchCategories();
    refetchLocations();
    refetchStaff();
    refetchResources();
  }, [refetchService, refetchCategories, refetchLocations, refetchStaff, refetchResources]);

  const openDrawer = (which: CardDrawer) => {
    setUnloaded({});
    setDrawer(which);
  };
  const closeDrawer = () => {
    setDrawer(null);
    setUnloaded({});
    // The page re-reads on close (a drawer may have saved some parts before one failed).
    refreshPage();
  };

  /** Save the drawer's parts in order; stop at the first that is refused or fails. */
  const saveParts = (parts: SavePart[]) => async (): Promise<boolean> => {
    for (const part of parts) {
      const handle = part.current;
      if (handle && !(await handle.save())) return false;
    }
    showToast('Changes saved', 'success');
    return true;
  };

  // ---- Header actions (More menu) ----
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function runAction(action: () => Promise<unknown>, done: string, failed: string) {
    setMenuAnchor(null);
    try {
      await action();
      refetchService();
      showToast(done, 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? failed, 'error');
    }
  }

  async function handleDuplicate(svc: V2Service) {
    setMenuAnchor(null);
    try {
      const copy = await copyService(svc.id);
      showToast('Service duplicated. The copy starts as a Draft.', 'success');
      if (copy) navigate(`/services/${copy.id}`);
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t duplicate the service.', 'error');
    }
  }

  async function handleRemove(svc: V2Service) {
    setConfirmRemove(false);
    try {
      await removeService(svc.id);
      showToast('Service removed', 'success');
      navigate('/services');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t remove the service.', 'error');
    }
  }

  const svc = service.data;

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/services"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All services
      </MuiLink>

      {service.status === 'loading' && !svc && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading service"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {service.status === 'error' && (
        <ScreenError
          error={service.error}
          what="the service"
          onRetry={service.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That service wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/services">
                Back to services
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {/* While the page re-reads after a drawer closes, the last values stay on screen. */}
      {service.status !== 'error' && svc && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Typography variant="h6" component="h1">
              {serviceName(svc)}
            </Typography>
            <ServiceStatusChip status={svc.status} />
            <Box sx={{ flex: 1 }} />
            <IconAction label="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Icon name="more_vert" />
            </IconAction>
            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => void handleDuplicate(svc)}>Duplicate</MenuItem>
              {svc.status === 'ACTIVE' ? (
                <MenuItem
                  onClick={() =>
                    void runAction(
                      () => deactivateService(svc.id),
                      'Service turned off',
                      'Couldn’t turn the service off.',
                    )
                  }
                >
                  Turn off
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() =>
                    void runAction(
                      // A Draft goes live by publishing it (DNKADM-0140); a turned-off
                      // service comes back by reactivating it.
                      () => (svc.status === 'DRAFT' ? publishService(svc.id) : reactivateService(svc.id)),
                      'Service turned on',
                      'Couldn’t turn the service on.',
                    )
                  }
                >
                  Turn on
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setConfirmRemove(true);
                }}
              >
                Remove
              </MenuItem>
            </Menu>
          </Stack>

          {/* ---- Empty-pool warnings ---- */}
          {warnings.status === 'ready' && warnings.data && warnings.data.length > 0 && (
            <Stack spacing={1} sx={{ mb: 3, maxWidth: 720 }}>
              {warnings.data.map((message, i) => (
                <Alert key={i} severity="warning" icon={<Icon name="warning" />}>
                  {message}
                </Alert>
              ))}
            </Stack>
          )}

          <Stack spacing={2} sx={{ maxWidth: 880, mb: 4 }}>
            <BasicsCard
              service={svc}
              categories={assignedView(assignedCategories, categoryList)}
              onEdit={() => openDrawer('basics')}
            />
            <WhereAndWhoCard
              service={svc}
              locations={assignedView(assignedLocations, locationList)}
              coaches={assignedView(assignedStaff, staffList)}
              bays={assignedView(assignedResources, resourceList)}
              onEdit={() => openDrawer('who')}
            />
            <BookingRulesCard
              service={svc}
              bucketNameById={bucketNameById}
              onEdit={() => openDrawer('rules')}
            />
            <VariationsPanel
              serviceId={serviceId}
              variationDisplay={svc.variationDisplay}
              onChanged={onLengthsChanged}
            />
            <SpecialPricesCard
              serviceId={serviceId}
              staffOptions={staffOptions}
              variationDisplay={svc.variationDisplay}
              lengthsVersion={lengthsVersion}
            />
          </Stack>

          <EditDrawer
            open={drawer !== null}
            title={drawer ? DRAWER_TITLES[drawer] : ''}
            onClose={closeDrawer}
            saveDisabled={anyUnloaded}
            onSave={
              drawer === 'basics'
                ? saveParts([formRef, categoriesRef])
                : drawer === 'who'
                  ? saveParts([formRef, locationsRef, staffRef, resourcesRef])
                  : saveParts([formRef])
            }
          >
            {drawer && (
              <Stack spacing={3}>
                <ServiceFields
                  key={drawer}
                  service={svc}
                  sections={[drawer]}
                  handleRef={formRef}
                  showToast={showToast}
                />
                {drawer === 'basics' && (
                  <AssignmentEditor
                    embedded
                    handleRef={categoriesRef}
                    onStatusChange={statusFor.categories}
                    serviceId={serviceId}
                    label="Category"
                    pickListLabel="categories"
                    list={categoryList}
                    getIds={getServiceCategories}
                    setIds={setServiceCategories}
                    showToast={showToast}
                  />
                )}
                {drawer === 'who' && (
                  <>
                    <AssignmentEditor
                      embedded
                      handleRef={locationsRef}
                      onStatusChange={statusFor.locations}
                      serviceId={serviceId}
                      label="Locations"
                      pickListLabel="locations"
                      list={locationList}
                      getIds={getServiceLocations}
                      setIds={setServiceLocations}
                      minOne
                      showToast={showToast}
                    />
                    {/* DNKADM-0395 U2 — a service that uses coaches must not lose all of them
                        in one click. "Not used" (NONE) has no pool to protect. */}
                    <AssignmentEditor
                      embedded
                      handleRef={staffRef}
                      onStatusChange={statusFor.staff}
                      serviceId={serviceId}
                      label="Coaches who teach this"
                      pickListLabel="coaches"
                      list={staffList}
                      getIds={getServiceStaff}
                      setIds={setServiceStaff}
                      confirmEmpty={
                        svc.staffAssignment === 'NONE'
                          ? undefined
                          : {
                              title: `Remove all coaches from ${serviceName(svc)}?`,
                              body: 'Bookings for this service will have no one to assign.',
                            }
                      }
                      showToast={showToast}
                    />
                    <AssignmentEditor
                      embedded
                      handleRef={resourcesRef}
                      onStatusChange={statusFor.resources}
                      serviceId={serviceId}
                      label="Bays"
                      pickListLabel="bays"
                      list={resourceList}
                      getIds={getServiceResources}
                      setIds={setServiceResources}
                      confirmEmpty={
                        svc.resourceAssignment === 'NONE'
                          ? undefined
                          : {
                              title: `Remove all bays from ${serviceName(svc)}?`,
                              body: 'Bookings for this service will have nothing to assign.',
                            }
                      }
                      showToast={showToast}
                    />
                  </>
                )}
              </Stack>
            )}
          </EditDrawer>

          <Dialog
            open={confirmRemove}
            onClose={() => setConfirmRemove(false)}
            aria-labelledby="remove-service-title"
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle id="remove-service-title">Remove {serviceName(svc)}?</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ fontSize: 14 }}>
                It leaves your services list. A service that has bookings can’t be removed; turn it
                off instead.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmRemove(false)} color="inherit">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={() => void handleRemove(svc)}>
                Remove
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

// ---- The three cards this file draws (Lengths and Special prices have their own files) ----

function BasicsCard({
  service,
  categories,
  onEdit,
}: {
  service: V2Service;
  categories: AssignedView;
  onEdit: () => void;
}) {
  const status = SERVICE_STATUS_WORDS[service.status];
  return (
    <SummaryCard title="Basics" onEdit={onEdit}>
      <SummaryRow label="Name" value={serviceName(service)} />
      <SummaryRow label="Category" value={listOr(categories, 'None')} />
      <SummaryRow label="Description" value={service.description ?? 'None'} />
      <SummaryRow
        label="Photo"
        value={
          service.imageUrl ? (
            <MuiLink href={service.imageUrl} target="_blank" rel="noopener noreferrer" variant="body2">
              View photo
            </MuiLink>
          ) : (
            'None'
          )
        }
      />
      <SummaryRow
        label="Featured in the member app"
        value={service.featured === undefined ? '—' : service.featured ? 'Featured' : 'Not featured'}
      />
      <SummaryRow
        label="Show lengths as"
        value={
          service.variationDisplay === undefined
            ? '—'
            : service.variationDisplay === 'duration'
              ? 'Durations'
              : 'Named types'
        }
      />
      <SummaryRow label="Status" value={`${status.label}. ${status.meaning}`} hint="Change it from More." last />
    </SummaryCard>
  );
}

function WhereAndWhoCard({
  service,
  locations,
  coaches,
  bays,
  onEdit,
}: {
  service: V2Service;
  locations: AssignedView;
  coaches: AssignedView;
  bays: AssignedView;
  onEdit: () => void;
}) {
  const notBookable = notBookableLine(service);
  // Collapsed only when all three sets have LOADED empty — never on a failed or pending read,
  // and never while the card has a can't-be-booked line to show (DNKADM-0498).
  const nothing =
    notBookable === null && isEmptySet(locations) && isEmptySet(coaches) && isEmptySet(bays);
  const usesCoach = service.staffAssignment !== 'NONE';
  const usesBay = service.resourceAssignment !== 'NONE';
  return (
    <SummaryCard
      title="Where and who"
      onEdit={onEdit}
      empty={nothing ? 'No locations, coaches or bays yet' : undefined}
    >
      {notBookable !== null && (
        <Typography variant="body2" sx={{ color: 'warning.main', mb: 0.5 }}>
          {notBookable}
        </Typography>
      )}
      <SummaryRow label="Locations" value={listOr(locations, 'No location')} />
      <SummaryRow label="Who picks the coach" value={pickModeLabel(service.staffAssignment)} />
      <SummaryRow
        label="Coaches who teach this"
        value={listOr(coaches, usesCoach ? 'No coach can take this' : 'No coach for this service')}
      />
      <SummaryRow label="Who picks the bay" value={pickModeLabel(service.resourceAssignment)} />
      <SummaryRow
        label="Bays"
        value={listOr(bays, usesBay ? 'No bay assigned' : 'No bay for this service')}
        last
      />
    </SummaryCard>
  );
}

function BookingRulesCard({
  service,
  bucketNameById,
  onEdit,
}: {
  service: V2Service;
  bucketNameById: Map<string, string>;
  onEdit: () => void;
}) {
  const payWith =
    service.creditBucketId === null
      ? 'None — free service'
      : (bucketNameById.get(service.creditBucketId) ?? '—');
  return (
    <SummaryCard title="Booking rules" onEdit={onEdit}>
      <SummaryRow
        label="Staff approval"
        value={
          service.requiresConfirmation ? (
            <Chip size="small" color="warning" label="Staff approve each booking" />
          ) : (
            'Not needed'
          )
        }
      />
      <SummaryRow
        label="Booking lead time"
        value={
          service.leadTimeMinutes === undefined
            ? 'Inherits the tenant lead time'
            : `${leadTimeMinutesLabel(service.leadTimeMinutes)} (override)`
        }
        hint={LEAD_TIME_BEHAVIOUR}
      />
      <SummaryRow label="Members pay with" value={payWith} />
      <SummaryRow label="Payment at booking" value={paymentModeSummary(service.paymentMode)} />
      <SummaryRow label="Guest rules" value={guestRules(service)} />
      <SummaryRow label="No-show fee" value="Set on each length, under Lengths and prices" />
      <SummaryRow label="Taxable" value={service.taxable ? 'Taxed at booking' : 'Not taxable'} last />
    </SummaryCard>
  );
}
